import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import isNil from 'lodash/isNil';
import * as queryString from 'query-string';
import { LoaderSpin, OnboardingContent, OnboardingFooter, OnboardingHeader } from 'components';
import { HTTPStatusCode } from 'config/const';
import { InviteService } from 'lib';
import { routeConstants } from 'routes';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { acceptInviteRequest, getInviteRequest, Invites, selectInvite } from 'store/Invites';
import { clearSignUpUser, selectSignUpUser } from 'store/SignUp';
import { IStore } from 'store/types';
import { User } from 'store/User';
import { selectUserInvites } from 'store/User/selectors';
import { EmployeeActionRequiredType, EmployeeRole, EmployeeStatus, IApiErrorPayloadWithCode, IInvite, InviteStatus } from 'types';
import { AcceptInvitationConflict } from './AcceptInvitationConflict';
import { InvitationAcceptedResult } from './AcceptInvitationResult';
import { messagingReview } from './Messaging';
import { ViewInvitation } from './ViewInvitation';

const AcceptInvitation = () => {
  const [ignoreAlertVisible, setIgnoreAlertVisible] = useState<boolean>(false);
  const signUpUser = useSelector(selectSignUpUser());
  const location = useLocation();
  const history = useHistory();
  const search = queryString.parse(location.search);
  const status = search.status as string;
  const dispatch = useDispatch();
  const invite = useSelector(selectInvite);
  const userInvites = useSelector(selectUserInvites);
  const isInviteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Invites.GET_INVITE_REQUEST));
  const isAcceptInviteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Invites.ACCEPT_INVITE_REQUEST));
  const userInvitesAccepted = userInvites?.every((invite) => invite.status === InviteStatus.ACCEPTED);
  const inviteToken = InviteService.getInviteToken();
  const inviteIgnored = InviteService.getInviteIgnored();

  useEffect(() => {
    // If a user manually navigates to `/accept-invitation` when they havent accepted their userInvites
    // OR when a user has ignored invites from `/accept-invitation`, route them to the Dashboard
    if ((!inviteToken && !userInvitesAccepted) || (inviteIgnored && !ignoreAlertVisible)) {
      history.replace(routeConstants.private.rootPath);
    }
  }, [userInvitesAccepted, inviteToken, inviteIgnored, ignoreAlertVisible]);

  useEffect(() => {
    // If window is refreshed, check storage for invite-token and refetch invite
    if (inviteToken && !invite) {
      dispatch(getInviteRequest(inviteToken));
    }
  }, [inviteToken, invite]);

  // Ignore Invite Alert
  const displayIgnoreAlert = () => {
    setIgnoreAlertVisible(true);
  };

  const pushRouteOnEnd = () => {
    if (signUpUser) {
      dispatch(clearSignUpUser());
      return history.replace(routeConstants.private.accountSetup.notifications);
    }

    return history.replace(routeConstants.private.rootPath);
  };

  const handleIgnoreInvite = () => {
    setIgnoreAlertVisible(false);
    InviteService.setInviteIgnored(true);
  };

  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Invites.ACCEPT_INVITE_REQUEST) || selectAPIRequestErrors(state, User.ACCEPT_ALL_USER_INVITES_REQUEST)) as IApiErrorPayloadWithCode;

  // We are only currently supporting single invites. There's code here that exists to support invitations with multiple sites

  const onAcceptInviteSuccess = (inviteResponse: IInvite) => {
    if (inviteResponse?.employee?.status === EmployeeStatus.APPROVED) {
      history.replace(routeConstants.private.acceptInvitationAccepted);
    }

    if (inviteResponse?.employee?.status === EmployeeStatus.ACTION_REQUIRED && inviteResponse?.employee?.actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL) {
      history.replace(routeConstants.private.acceptInvitationActionRequired);
    }
  };

  // Accept Invite
  const handleAcceptInvite = () => {
    if (invite) {
      return dispatch(acceptInviteRequest(invite.id, onAcceptInviteSuccess));
    }

    return null;
  };

  const statusParamToAcceptInvitationStatus: Record<string, EmployeeStatus> = {
    'action-required': EmployeeStatus.ACTION_REQUIRED,
    accepted: EmployeeStatus.APPROVED,
  };

  // API Error
  if (apiErrors?.statusCode === HTTPStatusCode.CONFLICT) {
    return (
      <OnboardingContent>
        <AcceptInvitationConflict />
      </OnboardingContent>
    );
  }

  return (
    <OnboardingContent>
      {isInviteLoading ? (
        <>
          <OnboardingHeader logoVisible title="Loading" message="One moment please..." />
          <OnboardingFooter>
            <LoaderSpin size={55} stroke={5} />
          </OnboardingFooter>
        </>
      ) : (
        <>
          {isNil(status) && invite && (
            <ViewInvitation
              invite={invite}
              messagingReview={messagingReview as (messageRole?: EmployeeRole, messageEntity?: string, isMultiInvite?: boolean) => JSX.Element | string | undefined}
              apiErrors={apiErrors}
              handleAcceptInvite={handleAcceptInvite}
              handleIgnoreInvite={handleIgnoreInvite}
              displayIgnoreAlert={displayIgnoreAlert}
              ignoreAlertVisible={ignoreAlertVisible}
              setIgnoreAlertVisible={setIgnoreAlertVisible}
              isLoading={isAcceptInviteLoading}
            />
          )}

          {((invite && status) || userInvitesAccepted) && <InvitationAcceptedResult status={statusParamToAcceptInvitationStatus[status]} pushRouteOnEnd={pushRouteOnEnd} />}
        </>
      )}
    </OnboardingContent>
  );
};

export default AcceptInvitation;
